import { ScrollingModule } from '@angular/cdk/scrolling';
import {
	AfterContentInit,
	Component,
	ComponentRef,
	ContentChildren,
	DestroyRef,
	Directive,
	EmbeddedViewRef,
	EventEmitter,
	inject,
	Input,
	OnDestroy,
	Output,
	QueryList,
	reflectComponentType,
	TemplateRef,
	Type,
	ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, map, skip, Subject, switchMap, take, takeUntil } from 'rxjs';

import { ClientScreen, ClientService } from '~app-client/core/services';
import { OnboardingService } from '~app-client-item/onboarding/services/onboarding.service';
import { OnboardingCondition } from '~app-client-item/onboarding/types/config';
import { ReachBottomMenuComponent } from '~app-ui/menus/bottom-menu';
import { MenuFloatingPosition } from '~ng-common-lib/ui/menu/components/floating/floating.component';

import { FloatingMenuComponent, WidthSize } from '../components/floating/floating.component';
import { PopupMenuComponent } from '../components/popup/popup.component';
import { MenuDesktopType } from '../types/desktop';
import { BaseMenu } from '../types/menu';

enum MenuType {
	FLOATING = 'FLOATING',
	POPUP = 'POPUP',
	MOBILE = 'MOBILE',
}

interface MenuDataMap {
	[MenuType.FLOATING]: {
		anchor: HTMLElement | null;
		widthSize: WidthSize | null;
		maxHeight: string | null;
		fullHeight: boolean;
		lightBg: boolean;
		bordered: boolean;
		hasPaddingTop: boolean;
		hasBackdrop: boolean | null;
		forcePosition: MenuFloatingPosition | null;
	};
	[MenuType.POPUP]: {
		lightBg: boolean;
		fullHeight: boolean;
		width: string | null;
		minHeight: string | null;
	};
	[MenuType.MOBILE]: {
		newDesign: true;
		transitionTimeMs?: number;
		showHeader: boolean;
		showFooter: boolean;
		fullHeight: boolean;
		lightBg: boolean;
	};
}

type MenuData<TYPE extends MenuType> = TYPE extends keyof MenuDataMap
	? MenuDataMap[TYPE]
	: Record<string, never>;

interface MenuDataToRecoverMap {
	[MenuType.MOBILE]: {
		transitionTimeMs: number;
	};
}

type MenuDataToRecover<TYPE extends MenuType> = TYPE extends keyof MenuDataToRecoverMap
	? MenuDataToRecoverMap[TYPE]
	: Record<string, never>;

const DATA_TO_RECOVER_MAP: Record<MenuType, Array<keyof MenuDataToRecover<MenuType>>> = {
	[MenuType.FLOATING]: [],
	[MenuType.POPUP]: [],
	[MenuType.MOBILE]: ['transitionTimeMs'],
};

@Directive({
	selector: '[reachMenuSection]',
	standalone: true,
	exportAs: 'reachMenuSection',
})
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
export class ReachMenuSectionDirective<T extends string> implements OnDestroy {
	private readonly clientService = inject(ClientService);
	private readonly viewContainerRef = inject(ViewContainerRef);
	private readonly templateRef = inject<TemplateRef<{ isMobile: boolean }>>(
		TemplateRef<{ isMobile: boolean }>
	);

	private readonly viewDestroyed$$ = new Subject<void>();

	private view?: EmbeddedViewRef<unknown>;
	private componentRef?: ComponentRef<BaseMenu>;
	private currentMenuType?: MenuType;

	@Input('reachMenuSection')
	public section: T | null;

	@Input('reachMenuSectionDesktopType')
	public desktopType!: MenuDesktopType;

	@Input('reachMenuSectionAlwaysUseDesktop')
	public alwaysUseDesktop = false;

	@Input('reachMenuSectionShowHeader')
	public showHeader?: boolean = true;

	@Input('reachMenuSectionShowFooter')
	public showFooter?: boolean;

	@Input('reachMenuSectionLightBg')
	public lightBg?: boolean;

	@Input('reachMenuSectionPopupWidth')
	public popupWidth?: string | null;

	@Input('reachMenuSectionMinHeight')
	public minHeight?: string | null;

	@Input('reachMenuSectionBordered')
	public bordered?: boolean;

	@Input('reachMenuSectionHasPaddingTop')
	public hasPaddingTop?: boolean = true;

	private _hasBackdrop?: boolean = true;

	@Input('reachMenuSectionHasBackdrop')
	public set hasBackdrop(hasBackdrop: boolean | undefined) {
		this._hasBackdrop = hasBackdrop;
	}

	public get hasBackdrop(): boolean {
		return this._hasBackdrop ?? true;
	}

	@Input('reachMenuSectionForcePosition')
	public forcePosition?: MenuFloatingPosition;

	private _anchor: HTMLElement | null = null;

	@Input('reachMenuSectionAnchor')
	public set anchor(anchor: HTMLElement | undefined) {
		this._anchor = anchor ?? null;
	}

	public get anchor(): HTMLElement | undefined {
		return this._anchor ?? undefined;
	}

	private _widthSize: WidthSize | null = null;

	@Input('reachMenuSectionWidthSize')
	public set widthSize(widthSize: WidthSize | undefined) {
		this._widthSize = widthSize ?? WidthSize.DEFAULT;
	}

	public get widthSize(): WidthSize | undefined {
		return this._widthSize ?? undefined;
	}

	@Input('reachMenuSectionDesktopFullHeight')
	public desktopFullHeight = false;

	private _maxHeight: string | null = null;

	@Input('reachMenuSectionMaxHeight')
	public set maxHeight(maxHeight: string | null) {
		this._maxHeight = maxHeight ?? null;
	}

	public get maxHeight(): string | null {
		return this._maxHeight;
	}

	private _fullHeight = false;

	@Input('reachMenuSectionFullHeight')
	public set fullHeight(fullHeight: boolean) {
		this._fullHeight = fullHeight;
	}

	public get fullHeight(): boolean {
		return this._fullHeight;
	}

	@Output('closed')
	public readonly closed$$ = new EventEmitter<void>();

	@Output('opened')
	public readonly opened$$ = new EventEmitter<void>();

	ngOnDestroy(): void {
		this.viewDestroyed$$.next();
		this.viewDestroyed$$.complete();
	}

	public close(immediatelly = false): void {
		if (this.currentMenuType === MenuType.MOBILE) {
			const instance = this.componentRef?.instance as ReachBottomMenuComponent;
			instance._emitToOnboarding = false;

			if (immediatelly) {
				instance.transitionTimeMs = 0;
			}
		}
		this.componentRef?.instance.close();
	}

	public open(immediatelly = false, anchor?: HTMLElement): void {
		this.currentMenuType = this.getMenuType();

		const { component, data } = this.getMenuFromType(
			this.currentMenuType,
			immediatelly,
			anchor
		);

		this.view = this.viewContainerRef.createEmbeddedView(this.templateRef, {
			isMobile: ClientService.screen === ClientScreen.MOBILE,
		});
		const projectableNodes = this.getProjectableNodes(component);

		this.componentRef = this.viewContainerRef.createComponent(component, {
			projectableNodes,
		});

		const instance = this.componentRef.instance;
		const dataToOverride = Object.entries(data);
		const keysToRecover = DATA_TO_RECOVER_MAP[this.currentMenuType];
		const dataToRecover = keysToRecover.map((key) => {
			const value = instance[key as keyof typeof instance];
			return {
				key,
				value,
			};
		});

		for (const [key, value] of dataToOverride) {
			(instance as unknown as Record<string, unknown>)[key] = value;
		}

		instance.opened$$.pipe(take(1), takeUntil(this.viewDestroyed$$)).subscribe(() => {
			for (const { key, value } of dataToRecover) {
				(instance as unknown as Record<string, unknown>)[key] = value;
			}

			this.componentRef?.changeDetectorRef.detectChanges();
			this.opened$$.emit();
		});

		instance.closed$$.pipe(take(1), takeUntil(this.viewDestroyed$$)).subscribe(() => {
			this.clear(true);
		});

		this.componentRef.changeDetectorRef.detectChanges();

		instance.open();

		// screen changes reaction
		// TODO: Add this to main menu instead of here.
		this.clientService.screen$.pipe(skip(1), takeUntil(this.viewDestroyed$$)).subscribe(() => {
			this.clear(true);
		});
	}

	public clear(emitClose = false): void {
		this.viewDestroyed$$.next();
		this.componentRef?.destroy();
		this.viewContainerRef.clear();
		this.view?.destroy();
		this.currentMenuType = undefined;

		if (emitClose) {
			this.closed$$.emit();
		}
	}

	private getProjectableNodes(component: Type<BaseMenu>): Node[][] {
		// https://angular.io/api/core/reflectComponentType
		const mirror = reflectComponentType(component);
		const selectors = mirror?.ngContentSelectors ?? ([] as string[]);

		const rootNodes = (this.view?.rootNodes ?? []) as Element[];
		const nodes = [...rootNodes];

		const projectableNodes: Element[][] = Array.from({ length: selectors.length }, () => []);
		const wildcardIdx = selectors.findIndex((selector) => selector === '*');

		// https://stackoverflow.com/a/61890918/6951173
		nodes.forEach((node: ChildNode) => {
			try {
				if (node instanceof Element) {
					const projectedToIdx = selectors.findIndex((selector) => {
						return node.getAttribute('ngProjectAs') === selector;
					});

					if (projectedToIdx !== -1) {
						projectableNodes[projectedToIdx].push(node);
						return;
					}
				}
			} catch {
				// ignore
			}

			if (wildcardIdx > -1) {
				projectableNodes[wildcardIdx].push(node as Element);
			}
		});

		return projectableNodes;
	}

	private getMenuFromType<TYPE extends MenuType>(
		type: TYPE,
		openImmediatelly: boolean,
		anchor?: HTMLElement
	): { component: Type<BaseMenu>; data: MenuData<TYPE> } {
		switch (type) {
			case MenuType.FLOATING: {
				const data: MenuData<MenuType.FLOATING> = {
					anchor: anchor ?? this.anchor ?? null,
					widthSize: this.widthSize ?? null,
					maxHeight: this.maxHeight,
					fullHeight: this.desktopFullHeight,
					lightBg: this.lightBg ?? false,
					bordered: this.bordered ?? false,
					hasPaddingTop: this.hasPaddingTop ?? true, // default: true
					hasBackdrop: this.hasBackdrop ?? true,
					forcePosition: this.forcePosition ?? null,
				};
				return {
					component: FloatingMenuComponent as Type<BaseMenu>,
					data,
				} as { component: Type<BaseMenu>; data: MenuData<TYPE> };
			}

			case MenuType.POPUP: {
				const data: MenuData<MenuType.POPUP> = {
					lightBg: this.lightBg ?? false,
					fullHeight: this.desktopFullHeight,
					width: this.popupWidth ?? null,
					minHeight: this.minHeight ?? null,
				};
				return { component: PopupMenuComponent as Type<BaseMenu>, data } as {
					component: Type<BaseMenu>;
					data: MenuData<TYPE>;
				};
			}

			default: {
				const data: MenuData<MenuType.MOBILE> = {
					newDesign: true,
					transitionTimeMs: 0,
					showHeader: this.showHeader ?? true, // default: true
					showFooter: !!this.showFooter, // default: false,
					fullHeight: this.fullHeight, // default: false,
					lightBg: this.lightBg ?? false, // default: false,
				};
				if (!openImmediatelly) {
					delete data.transitionTimeMs;
				}
				return {
					component: ReachBottomMenuComponent as Type<BaseMenu>,
					data,
				} as { component: Type<BaseMenu>; data: MenuData<TYPE> };
			}
		}
	}

	private getMenuType(): MenuType {
		const screen = ClientService.screen;

		if (!this.alwaysUseDesktop && screen === ClientScreen.MOBILE) {
			return MenuType.MOBILE;
		}
		if (this.desktopType === MenuDesktopType.FLOATING) {
			return MenuType.FLOATING;
		} else {
			return MenuType.POPUP;
		}
	}
}

/**
 * **Limitations**
 * - You can't directly use <ng-container> as a direct children
 * of the [reachMenuSection] directive.
 * - You can't directly use *ngIf (or other structural directives)
 * as a direct children of the [reachMenuSection] directive.
 *
 * ---
 *
 * **Tips**
 * - Always wrap the title and body of the menu in a
 * `<div>` tag (except `<ng-container>`) without structural directives.
 *
 * ---
 *
 * **Usage**
 *
 * If you want to use the asterisc notation, you need to
 * add the directive to an `ng-container` element.
 *
 * ```html
 * <reach-menu #menu [activeSection]="activeSection">
 * 	 <ng-container *reachMenuSection="sectionId; desktopType: sectionDesktopType; anchor: sectionAnchor">
 * 	   ...
 *   </ng-container>
 * </reach-menu>
 * ```
 *
 * Else, you can use the directive in a ng-template using the regular
 * directive notation.
 *
 * ```html
 * <reach-menu #menu [activeSection]="activeSection">
 * 	 <ng-template
 * 	 	 [reachMenuSection]="sectionId"
 * 	 	 [reachMenuSectionDesktopType]="sectionDesktopType"
 * 	 	 [reachMenuAnchor]="sectionAnchor"
 *   >
 * 	   ...
 *   </ng-template>
 * </reach-menu>
 * ```
 *
 * ---
 *
 * **Example**
 * ```html
 * <button #anchor (click)="activeSection = activeSection === '1' ? '2' : '1'">
 *   Toggle Section
 * </button>
 *
 * <reach-menu #menu [activeSection]="activeSection">
 *   <ng-container *reachMenuSection="'1'; desktopType: sectionDesktopType; anchor: sectionAnchor">
 *     <div ngProjectAs=[~left]>Left 1</div>
 *     <div ngProjectAs=[~title]>Title 1</div>
 *     <div ngProjectAs=[~right]>Right 1</div>
 *     <div>This is the section 1 body</div>
 *   </ng-container>
 *   <ng-container *reachMenuSection="'2'; desktopType: sectionDesktopType; anchor: sectionAnchor">
 *     <div ngProjectAs=[~left]>Left 2</div>
 *     <div ngProjectAs=[~title]>Title 2</div>
 *     <div ngProjectAs=[~right]>Right 2</div>
 *     <div>This is the section 2 body</div>
 *   </ng-container>
 * </reach-menu>
 * ```
 */
@Component({
	selector: 'reach-menu',
	template: '<ng-content></ng-content>',
	styles: [':host { display: none; }'],
	standalone: true,
	imports: [ScrollingModule],
})
export class ReachMenuComponent<T extends string = string>
	extends BaseMenu
	implements AfterContentInit, OnDestroy
{
	private readonly destroyRef = inject(DestroyRef);
	private readonly onboardingService = inject(OnboardingService);

	@ContentChildren(ReachMenuSectionDirective<T>)
	protected readonly sectionList: QueryList<ReachMenuSectionDirective<T>> | undefined;

	private currentSection?: ReachMenuSectionDirective<T>;

	private readonly activeSection$$ = new BehaviorSubject<T | null>(null);
	private readonly activeSection$ = this.activeSection$$.asObservable();

	private readonly componentChanged$$ = new Subject<void>();
	private _onboardingConditionAdded = false;

	@Input()
	public set activeSection(section: T | null) {
		this.activeSection$$.next(section);
	}

	public get activeSection(): T | null {
		return this.activeSection$$.value;
	}

	@Output('closed')
	public readonly closed$$ = new EventEmitter<void>();

	@Output('opened')
	public readonly opened$$ = new EventEmitter<void>();

	private alreadyOpened = false;
	private _fullyOpened = false;

	private set fullyOpened(fullyOpened: boolean) {
		this._fullyOpened = fullyOpened;
	}

	public get fullyOpened(): boolean {
		return this._fullyOpened;
	}

	ngAfterContentInit(): void {
		this.sectionList?.changes
			.pipe(
				map(() => {
					return this.sectionList?.toArray();
				}),
				switchMap((sections) => {
					return this.activeSection$.pipe(
						map((activeSectionKey) => {
							return (
								sections?.find((section) => {
									return section.section === activeSectionKey;
								}) ??
								sections?.find((section) => {
									return !section.section;
								}) ??
								null
							);
						})
					);
				}),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe((activeSection) => {
				this.changeActiveSection(activeSection);
			});

		this.sectionList?.notifyOnChanges();
	}

	ngOnDestroy(): void {
		this.componentChanged$$.next();
		this.componentChanged$$.complete();
	}

	open(anchor?: HTMLElement): void {
		if (!this.alreadyOpened) {
			this.alreadyOpened = true;
			this.listenForOpen(this.currentSection ?? null);
			this.listenForClose(this.currentSection ?? null);

			this.currentSection?.open(false, anchor);
		}
	}

	close(): void {
		this.alreadyOpened = false;
		this.fullyOpened = false;
		this.currentSection?.close();
	}

	public _emitOpen(): void {
		this.opened$$.emit();
		this.alreadyOpened = true;
		this.fullyOpened = true;

		this.onboardingService.addCondition(OnboardingCondition.GENERIC_MENU_FULLY_OPENED);
		this._onboardingConditionAdded = true;
	}

	public _emitClose(): void {
		this.closed$$.emit();
		this.alreadyOpened = false;
		this.fullyOpened = false;

		if (this._onboardingConditionAdded) {
			this.onboardingService.removeCondition(OnboardingCondition.GENERIC_MENU_FULLY_OPENED);
			this._onboardingConditionAdded = false;
		}
	}

	private changeActiveSection(section: ReachMenuSectionDirective<T> | null): void {
		this.componentChanged$$.next();

		const prev = this.currentSection;
		this.currentSection = section ?? undefined;

		if (this.alreadyOpened) {
			// Close prev, open current

			const sectionToListenTo = section ?? prev;
			this.listenForClose(sectionToListenTo ?? null);

			if (section) {
				prev?.clear();
			} else {
				prev?.close();
			}

			section?.open(this.fullyOpened);
		}
	}

	private listenForClose(section: ReachMenuSectionDirective<T> | null): void {
		section?.closed$$.pipe(take(1), takeUntil(this.componentChanged$$)).subscribe(() => {
			this._emitClose();
		});
	}

	private listenForOpen(section: ReachMenuSectionDirective<T> | null): void {
		section?.opened$$.pipe(take(1), takeUntil(this.componentChanged$$)).subscribe(() => {
			this._emitOpen();
		});
	}
}
